import * as React from "react";
import { ReactRouterStubsContext } from "~/contexts/reactRouterStubsContext";
import { useSafeClerkReact } from "~/hooks/useSafeClerk";

export const useLogout = () => {
  const clerk = useSafeClerkReact();
  const stubs = React.useContext(ReactRouterStubsContext);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const isSubmittingRef = React.useRef(false);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getLogoutRedirectUrl = React.useCallback(() => {
    return stubs.location.pathname.startsWith("/marketplace")
      ? stubs.location.pathname + stubs.location.search + stubs.location.hash
      : undefined;
  }, [stubs.location]);

  const handleLogoutSubmit = React.useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      // If we're already submitting programmatically, allow the form to submit normally
      if (isSubmittingRef.current) {
        isSubmittingRef.current = false;
        return;
      }

      e.preventDefault();
      setIsLoggingOut(true);

      try {
        if (clerk) {
          await clerk.signOut({
            redirectUrl: getLogoutRedirectUrl(),
          });
        }
      } catch {
        // Clerk signout failed, reset state if component is still mounted
        if (isMountedRef.current) {
          setIsLoggingOut(false);
        }
        return;
      }

      // Use requestSubmit() to properly trigger form submission with validation
      // Only proceed if component is still mounted (user hasn't navigated away)
      if (isMountedRef.current) {
        isSubmittingRef.current = true;
        e.currentTarget.requestSubmit();
      }
    },
    [clerk, getLogoutRedirectUrl],
  );

  return {
    isLoggingOut,
    handleLogoutSubmit,
  };
};
